import React, { useState, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import * as moment from "moment";
import ClipLoader from "react-spinners/ClipLoader";
import UserInfo from "@utils/UserInfo";
import { Button, WarningPopUp } from "hesus-ui-kit";
import { renderPriceWithCurrencyWithoutUnit } from "@utils/utils";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import InvoiceDocument from "./components/InvoiceDocument/InvoiceDocument";
import CompanySite from "./components/CompanySite/CompanySite";
import { INVOICING_CONTACT_TYPE_ID } from "./components/CompanySite/Recipients";
import FinancialInformation from "./components/FinancialInformation";
import AdditionalDocumentInformation from "./components/AdditionalDocumentInformation";
import InvoiceServices from "./components/InvoiceServices";
import { RequiredMessagePara } from "@utils/extra-style";
import NotFound from "@components/Errors/NotFound";
import {
  getInvoiceStatus,
  getInvoiceStatusOptions,
} from "./components/InvoiceDocument/InvoiceStatusFlow";

import {
  MainViewContainer,
  SectionContainer,
  OuterSection,
  Title,
  GridRow,
} from "./AddEditInvoice.styled";

const AddEditInvoice = ({ t, isEditMode }) => {
  const params = useParams();
  const history = useNavigate();
  const location = useLocation();
  const countryId = UserInfo.countryId();
  const userInfo = UserInfo.getUser();
  const invoiceId = parseInt(params.id, 10);

  const [statusId, setStatusId] = useState(0);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const generalParams = useGetGeneralParameters();
  const [servicesList, setServicesList] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [serviceItems, setServiceItems] = useState([]);
  const [exchangeCurrencies, setExchangeCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [vatRateList, setVatRateList] = useState([]);
  const [paymentTermsList, setPaymentTermsList] = useState([]);
  const [hesusCatalogList, setHesusCatalogList] = useState([]);
  const [extraItems, setExtraItems] = useState([]);
  const [serviceItemsErrors, setServiceItemsErrors] = useState([]);
  const [isNotFound, setIsNotFound] = useState(false);
  const [descriptionData, setDescriptionData] = useState({
    clientCommentBefore: "",
    clientCommentAfter: "",
    internalComment: "",
  });
  const [serviceLoading, setServiceLoading] = useState(false);
  const [isCommentDataLoaded, setIsCommentDataLoaded] = useState(false);

  const [serviceSectionError, setServiceSectionError] = useState([]);
  const [extraItemsErrors, setExtraItemsErrors] = useState([]);

  const [isConfirmVisible, setConfirmVisibility] = useState(false);
  const [isConfirm, setActionConfirmStatus] = useState(false);

  const [realisedQuantityLoading, setRealisedQuantityLoading] = useState({});
  const [exchangeRateErrors, setExchangeRateErrors] = useState({});

  const { countryCurrencyPosition, unitList } = generalParams;

  const [formData, setFormData] = useState({
    id: { required: true, value: isEditMode ? invoiceId : "", error: "" },
    countryId: {
      required: true,
      value: !isEditMode ? countryId : "",
      error: "",
    },
    documentType: { required: true, value: 1, error: "" },
    startDate: {
      required: true,
      value: moment().format("YYYY-MM-DD"),
      error: "",
    },
    endDate: {
      required: false,
      value: moment().format("YYYY-MM-DD"),
      error: "",
    },
    invoiceType: { required: true, value: [], error: "" },
    attachedInvoice: { required: true, value: [], error: "" },
    orderNumber: { required: true, value: "", error: "" },
    statusId: { required: true, value: 0, error: "" },
    sendByEmail: { required: true, value: true, error: "" },
    documentNumber: { required: true, value: "", error: "" },
    company: { required: true, value: null, error: "" },
    site: { required: true, value: null, error: "" },
    delegatedCompany: { required: false, value: null, error: "" },
    recipients: { required: false, value: [], error: "" },
    currencyId: { required: true, value: "", error: "" },
    paymentTermId: { required: true, value: "", error: "" },
    isSubcontracting: { required: true, value: false, error: "" },
    vatRateId: { required: true, value: "", error: "" },
    bankAccountId: { required: true, value: "", error: "" },
    dueDate: { required: false, value: "", error: "" },
    hasAppendedSummaries: { required: false, value: false, error: "" },
    linkToSummaries: { required: false, value: "", error: "" },
  });

  const mapInvoiceToForm = (invoice, countryId) => {
    setFormData({
      id: {
        required: true,
        value: invoice?.id ?? "",
        error: "",
      },
      countryId: {
        required: true,
        value: invoice.country?.id ?? countryId,
        error: "",
      },
      documentType: {
        required: true,
        value: invoice.documentType,
        error: "",
      },
      startDate: {
        required: true,
        value: invoice.periodStart
          ? moment(invoice.periodStart).format("YYYY-MM-DD")
          : "",
        error: "",
      },
      endDate: {
        required: false,
        value: invoice.periodEnd
          ? moment(invoice.periodEnd).format("YYYY-MM-DD")
          : "",
        error: "",
      },
      invoiceType: {
        required: true,
        value: [
          ...(invoice.isInterco ? [1] : []),
          ...(invoice.isRegularisation ? [2] : []),
        ],
        error: "",
      },
      attachedInvoice: {
        required: true,
        value: invoice?.attachedInvoice
          ? {
              id: invoice.attachedInvoice.id,
              name: invoice.attachedInvoice.documentNumber,
              price: renderPriceWithCurrencyWithoutUnit(
                Number(invoice.attachedInvoice.totalExcludedTaxes ?? 0).toFixed(
                  2
                ),
                invoice.currency?.symbol,
                invoice.currency?.position
              ),
            }
          : "",
        error: "",
      },
      orderNumber: {
        required: true,
        value: invoice.clientOrderNumber,
        error: "",
      },
      statusId: {
        required: true,
        value: invoice.status,
        error: "",
      },
      sendByEmail: {
        required: true,
        value: invoice.isSentByEmail,
        error: "",
      },
      documentNumber: {
        required: true,
        value: invoice.documentNumber,
        error: "",
      },
      company: {
        required: true,
        value: invoice.company ?? null,
        error: "",
      },
      site: {
        required: true,
        value: invoice.site ?? null,
        error: "",
      },
      delegatedCompany: {
        required: false,
        value:
          invoice.delegatedCompany ?? invoice.site?.delegatedCompany ?? null,
        error: "",
      },
      recipients: {
        required: false,
        value: invoice.recipients ?? [],
        error: "",
      },
      currencyId: {
        required: true,
        value: invoice.currency?.id,
        error: "",
      },
      paymentTermId: {
        required: true,
        value: invoice.paymentTerm?.id ?? "",
        error: "",
      },
      isSubcontracting: {
        required: true,
        value: invoice.isSubcontracting,
        error: "",
      },
      vatRateId: {
        required: true,
        value: invoice.vatRate?.id ?? "",
        error: "",
      },
      bankAccountId: {
        required: true,
        value: invoice.bankAccount?.id ?? "",
        error: "",
      },
      dueDate: {
        required: true,
        value: invoice.dueDate
          ? moment(invoice.dueDate).format("YYYY-MM-DD")
          : "",
        error: "",
      },
      hasAppendedSummaries: {
        required: true,
        value: invoice.hasAppendedSummaries,
        error: "",
      },
      linkToSummaries: {
        required: true,
        value: invoice.linkToSummaries ?? "",
        error: "",
      },
    });
    setStatusId(invoice.status);
    setDescriptionData({
      clientCommentBefore: invoice.clientCommentBefore || "",
      clientCommentAfter: invoice.clientCommentAfter || "",
      internalComment: invoice.internalComment || "",
    });
    setIsCommentDataLoaded(true);
  };

  const fetchInvoiceData = (id) => {
    setLoadingInvoice(true);
    setApiError("");
    setIsNotFound(false);
    axios
      .get(`/clientInvoices/${id} `)
      .then((response) => {
        const invoice = response.data?.[0];
        if (!invoice) {
          setIsNotFound(true);
          return;
        }
        setInvoiceItems(invoice?.items || []);
        mapInvoiceToForm(invoice, countryId);
        setExchangeRates(invoice?.exchangeRates || []);
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          setIsNotFound(true);
          return;
        }

        setApiError(error.response?.data?.message ?? "Failed to load invoice");
      })
      .finally(() => setLoadingInvoice(false));
  };

  useEffect(() => {
    if (!isEditMode || !invoiceId) return;

    fetchInvoiceData(invoiceId);
  }, [invoiceId, isEditMode]);

  useEffect(() => {
    if (
      isEditMode ||
      Object.keys(generalParams).length === 0 ||
      !formData.countryId.value
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      currencyId: {
        ...prev.currencyId,
        value: generalParams?.mainCurrencyList?.[prev.countryId.value] ?? "",
        error: "",
      },
    }));
  }, [generalParams, isEditMode]);

  const fetchCatalogService = () => {
    axios
      .get(
        `/general/hesusCatalog?invoicing=true&countries=[${formData.countryId.value}]`
      )
      .then((response) => {
        const catalogData = response.data.map((catalog) => {
          return {
            label: catalog.label,
            value: catalog.id,
          };
        });
        setHesusCatalogList(catalogData);
      })
      .catch(() => {
        setHesusCatalogList([]);
      });
  };

  useEffect(() => {
    fetchCatalogService();
  }, [formData.countryId.value]);

  const calculateDueDate = (paymentTerm, periodEndDate) => {
    if (!paymentTerm) {
      return "";
    }
    
    const currentEndDate = moment().format("YYYY-MM-DD");
    
    const calculationValue = Number(paymentTerm.calculationValue || 0);
    switch (paymentTerm.calculationCode) {
      case "DAYS":
      case "DAYS_INVOICE_DATE":
        return moment(currentEndDate, "YYYY-MM-DD")
          .add(calculationValue, "days")
          .format("YYYY-MM-DD");
      case "DAYS_END_OF_MONTH":
        if (!periodEndDate) {
          return "";
        }
        return moment(periodEndDate, "YYYY-MM-DD")
          .endOf("month")
          .add(calculationValue, "days")
          .format("YYYY-MM-DD");
      case "SPLIT_ORDER_INVOICE":
        return moment(currentEndDate, "YYYY-MM-DD");
      default:
        return "";
    }
  };

  // Preselect only the contacts available in both company and site
  const filterAndSetInvoicingRecipients = (companyId, contacts) => {
    const candidates = (contacts || []).filter((c) => c?.contactId || c?.id);
    if (!companyId || candidates.length === 0) {
      setFormData((current) => ({
        ...current,
        recipients: { ...current.recipients, value: [], error: "" },
      }));
      return;
    }
    axios
      .get(
        `/allContacts?companyId=${companyId}&typeIds=[${INVOICING_CONTACT_TYPE_ID}]`
      )
      .then((response) => {
        const invoicingContactIds = new Set(
          (response.data?.contacts || []).map((c) => String(c.id))
        );
        const filteredContacts = candidates
          .filter((c) => invoicingContactIds.has(String(c.contactId ?? c.id)))
          .map((c) => ({ ...c, id: null, contactId: c.contactId ?? c.id }));
        setFormData((current) => ({
          ...current,
          recipients: {
            ...current.recipients,
            value: filteredContacts,
            error: "",
          },
        }));
      })
      .catch(() => {});
  };

  const onFormChange = (key, value, options = {}) => {
    const { isCheckboxGroup = false } = options;
    let recipientsToFilter = null;
    setFormData((prev) => {
      let newValue = value;
      if (isCheckboxGroup) {
        const current = prev[key].value || [];
        newValue = value.checked
          ? [...current, value.id]
          : current.filter((item) => item !== value.id);
      }

      const updatedFormData = {
        ...prev,
        [key]: {
          ...prev[key],
          value: newValue,
          error: "",
          required: false,
        },
      };

      // When site changes, update dependent fields
      if (key === "site") {
        const paymentTerm = value?.paymentTerms;
        updatedFormData.paymentTermId = {
          ...prev.paymentTermId,
          value: paymentTerm?.id ?? "",
          error: "",
          required: false,
        };
        updatedFormData.vatRateId = {
          ...prev.vatRateId,
          value: value?.invoicingVatRate?.id ?? "",
          error: "",
          required: false,
        };
        updatedFormData.dueDate = {
          ...prev.dueDate,
          value: calculateDueDate(paymentTerm, prev.endDate?.value),
          error: "",
          required: false,
        };
        if (prev.sendByEmail.value && value?.contacts) {
          recipientsToFilter = {
            companyId: value?.company?.id ?? prev.company?.value?.id,
            contacts: value.contacts,
          };
        }
      }

      if (key === "paymentTermId") {
        const paymentTerm = options?.paymentTerm;
        const calculatedDueDate = calculateDueDate(
          paymentTerm,
          prev.endDate?.value
        );
        updatedFormData.dueDate = {
          ...prev.dueDate,
          value: calculatedDueDate,
          error: calculatedDueDate === "" ? prev.dueDate.error : "",
        };
      }

      if (key === "endDate" && prev.paymentTermId?.value) {
        const paymentTermId = prev.paymentTermId?.value;
        const paymentTerm = paymentTermsList.find(
          (payment) => Number(payment.value) === Number(paymentTermId)
        );
        const calculatedDueDate = calculateDueDate(paymentTerm, value);
        updatedFormData.dueDate = {
          ...prev.dueDate,
          value: calculatedDueDate,
          error: calculatedDueDate === "" ? prev.dueDate.error : "",
        };
      }

      // When enabling sendByEmail, pre-select site invoicing contacts
      if (key === "sendByEmail" && value && prev.site?.value?.contacts) {
        recipientsToFilter = {
          companyId: prev.company?.value?.id,
          contacts: prev.site.value.contacts,
        };
      }

      // When country changes, reset dependent fields
      if (key === "countryId") {
        updatedFormData.paymentTermId = {
          ...prev.paymentTermId,
          value: "",
          error: "",
          required: false,
        };
        updatedFormData.dueDate = {
          ...prev.dueDate,
          value: "",
          error: "",
          required: false,
        };
        updatedFormData.vatRateId = {
          ...prev.vatRateId,
          value: "",
          error: "",
          required: false,
        };
        updatedFormData.bankAccountId = {
          ...prev.bankAccountId,
          value: "",
          error: "",
          required: false,
        };
        updatedFormData.company = {
          ...prev.company,
          value: "",
          error: "",
          required: false,
        };
        updatedFormData.site = {
          ...prev.site,
          value: "",
          error: "",
          required: false,
        };
        updatedFormData.recipients = {
          ...prev.recipients,
          value: "",
          error: "",
          required: false,
        };
        updatedFormData.isSubcontracting = {
          ...prev.isSubcontracting,
          value: false,
          error: "",
          required: false,
        };
        updatedFormData.currencyId = {
          ...prev.currencyId,
          value: generalParams?.mainCurrencyList?.[value],
          error: "",
        };
      }
      return updatedFormData;
    });
    if (recipientsToFilter) {
      filterAndSetInvoicingRecipients(
        recipientsToFilter.companyId,
        recipientsToFilter.contacts
      );
    }
    if (key === "countryId") {
      setServiceItems([]);
      setExtraItems([]);
      setServicesList([]);
      setInvoiceItems([]);
    }
  };

  const prepareServiceItems = (services, invoiceItems) => {
    const preparedServices = services.map((service) => ({
      quoteService: {
        id: service.id,
        label: service.label,
        currency: service.currency,
      },

      quoteItems: service.quoteitems.map((quoteItem) => {
        const invoiceItem = invoiceItems.find(
          (item) =>
            item.quoteService?.id === service.id &&
            item.quoteItem?.id === quoteItem.id &&
            item.isExtra === false
        );

        return {
          id: invoiceItem?.id ?? null,
          clientInvoiceItemId: invoiceItem?.id ?? null,
          quoteItemId: invoiceItem?.quoteItem?.id ?? quoteItem.id,
          quoteItemLabel: invoiceItem?.serviceLabel ?? quoteItem.label,
          quoteItemUnit: invoiceItem?.unit ?? quoteItem.unit,
          quantityRealised: invoiceItem?.quantityRealised ?? 0,
          unitRealised: invoiceItem?.unitRealised ?? quoteItem.unit,
          hesusService: quoteItem.hesusService,
          product: quoteItem.product ?? [],
          transport: quoteItem.transport ?? [],
          sellingPrice: invoiceItem?.sellingPrice ?? quoteItem?.sellingPrice,
          sellingUnit: invoiceItem?.sellingUnit ?? quoteItem?.unit,
          invoicedUnit: invoiceItem?.invoicedUnit ?? quoteItem.unit,
          quantity: invoiceItem?.invoicedQuantity ?? "",
          realisedQuantityLoading: false,
          realisedQuantityData: null,
          clientInvoiceItemSummaries: invoiceItem?.clientInvoiceItemSummaries,
          isExtra: false,
        };
      }),
    }));

    const extraItems = invoiceItems
      .filter((item) => item.isExtra === true)
      .map((item) => ({
        id: item.id ?? null,
        quoteServiceId: item.quoteService?.id ?? null,
        hesusCatalogId: item?.hesusCatalog?.id ?? null,
        quoteItemId: null,
        serviceLabel: item.serviceLabel ?? "",
        clientComment: item.clientComment ?? "",
        quantity: item.invoicedQuantity ?? "",
        sellingPrice: item.sellingPrice ?? "",
        sellingUnit: item.sellingUnit ?? "",
        invoicedUnit: item.invoicedUnit ?? "",
        quantityRealised: item.quantityRealised ?? "",
        isExtra: true,
        isFreeText: item.hesusCatalog?.id == null,
        showClientComment: !!item.clientComment?.trim(),
      }));

    return {
      services: preparedServices,
      extraItems,
    };
  };

  const handleAddExtraItem = () => {
    setExtraItems((prev) => [
      ...prev,
      {
        id: "",
        quoteServiceId: null,
        hesusCatalogId: null,
        quoteItemId: null,
        serviceLabel: "",
        clientComment: "",
        quantity: "",
        sellingPrice: "",
        sellingUnit: "",
        invoicedUnit: "",
        quantityRealised: 0,
        isFreeText: false,
        isExtra: true,
        showClientComment: false,
      },
    ]);
  };

  const handleFreeTextClick = (index) => {
    setExtraItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              isFreeText: true,
              hesusCatalogId: null,
            }
          : item
      )
    );
  };

  const handleDeleteExtraItem = (index) => {
    setExtraItems((prev) => prev.filter((_, i) => i !== index));
  };

  const hasExtraItemErrors = (errors) =>
    errors.some((itemErrors) => Object.values(itemErrors || {}).some(Boolean));

  const handleExtraItemChange = (index, field, value) => {
    setExtraItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: value,
            }
          : item
      )
    );

    setExtraItemsErrors((prev) => {
      const updatedErrors = prev.map((errors, i) =>
        i === index
          ? {
              ...errors,
              [field]: null,
            }
          : errors
      );
      // Only clear section error if there are NO errors remaining
      if (!hasExtraItemErrors(updatedErrors)) {
        setServiceSectionError([]);
      }
      return updatedErrors;
    });
  };

  const loadRealisedQuantity = async (quoteService, quoteItem) => {
    const quoteItemId = quoteItem?.quoteItemId;

    setRealisedQuantityLoading((prev) => ({
      ...prev,
      [quoteItemId]: true,
    }));

    try {
      const response = await axios.get("/clientInvoices/realisedQuantity", {
        params: {
          quoteService: quoteService.id,
          quoteItemId: quoteItemId,
          clientInvoiceItemId: quoteItem?.clientInvoiceItemId,
          startDate: moment(formData.startDate.value).format("YYYY-MM-DD"),
          endDate: moment(formData.endDate.value).format("YYYY-MM-DD"),
          readyInvoicingOnly: true,
        },
      });
      const totalSelected = response.data?.totalSelected ?? 0;
      setServiceItems((prevItems) =>
        prevItems.map((serviceItem) => ({
          ...serviceItem,
          quoteItems: serviceItem.quoteItems?.map((quoteItem) => {
            if (quoteItem.quoteItemId !== quoteItemId) {
              return quoteItem;
            }
            return {
              ...quoteItem,
              quantityRealised: totalSelected,
              realisedQuantityData: {
                ...response.data,
                summaries: response.data.summaries.map((summary, index) => ({
                  ...summary,
                  realisedQuantityId: `${quoteItem.id}-${summary.clientOrderServiceItemId}-${summary.clientOrderDate}-${index}`,
                })),
              },
            };
          }),
        }))
      );
    } catch (error) {
      console.error(
        `Error loading realised quantity for quote item ${quoteItemId}`,
        error
      );
    } finally {
      setRealisedQuantityLoading((prev) => ({
        ...prev,
        [quoteItemId]: false,
      }));
    }
  };

  const loadRealisedQuantities = async (services) => {
    const requests = [];
    if (formData.documentType.value === 1) {
      services.forEach((service) => {
        service.quoteItems?.forEach((quoteItem) => {
          requests.push(loadRealisedQuantity(service.quoteService, quoteItem));
        });
      });
    }

    await Promise.all(requests);
  };

  const loadRealisedQuantityForPopup = async (quoteService, quoteItem) => {
    const quoteItemId = quoteItem?.quoteItemId;

    setRealisedQuantityLoading((prev) => ({
      ...prev,
      [quoteItemId]: true,
    }));

    try {
      const response = await axios.get("/clientInvoices/realisedQuantity", {
        params: {
          quoteService: quoteService.id,
          quoteItemId,
          clientInvoiceItemId: quoteItem.clientInvoiceItemId,
          startDate: moment(formData.startDate.value).format("YYYY-MM-DD"),
          endDate: moment(formData.endDate.value).format("YYYY-MM-DD"),
          readyInvoicingOnly: true,
        },
      });

      setServiceItems((prevItems) =>
        prevItems.map((serviceItem) => ({
          ...serviceItem,
          quoteItems: serviceItem.quoteItems?.map((item) => {
            if (item.quoteItemId !== quoteItemId) {
              return item;
            }

            return {
              ...item,
              // IMPORTANT:
              // Do NOT update quantityRealised here.
              realisedQuantityData: {
                ...response.data,
                summaries: (response.data?.summaries || []).map(
                  (summary, index) => ({
                    ...summary,
                    realisedQuantityId: `${item.id}-${summary.clientOrderServiceItemId}-${summary.clientOrderDate}-${index}`,
                  })
                ),
              },
            };
          }),
        }))
      );
    } catch (error) {
      console.error(
        `Error loading realised quantity for popup for quote item ${quoteItemId}`,
        error
      );
    } finally {
      setRealisedQuantityLoading((prev) => ({
        ...prev,
        [quoteItemId]: false,
      }));
    }
  };

  const handleRealisedQuantityPopupOpen = (quoteService, quoteItem) => {
    if (
      !isEditMode ||
      !quoteItem?.clientInvoiceItemSummaries?.length ||
      quoteItem?.realisedQuantityData
    ) {
      return;
    }

    return loadRealisedQuantityForPopup(quoteService, quoteItem);
  };

  useEffect(() => {
    if (
      !servicesList.length &&
      !invoiceItems.length &&
      !formData.startDate.value &&
      !formData.endDate.value
    ) {
      return;
    }

    // refresh realised quantities based on the new period
    loadRealisedQuantities(serviceItems);
  }, [
    formData.startDate.value,
    formData.endDate.value,
    formData.documentType.value,
  ]);

  useEffect(() => {
    if (!servicesList.length && !invoiceItems.length) {
      setServiceItems([]);
      setExtraItems([]);
      return;
    }
    const { services, extraItems } = prepareServiceItems(
      servicesList,
      invoiceItems
    );
    setServiceItems(services);
    setExtraItems(extraItems);

    // Render service items immediately
    const quoteServicesWithoutSummaries = services
      .map((service) => ({
        ...service,
        quoteItems: service.quoteItems?.filter(
          (quoteItem) => !quoteItem.clientInvoiceItemSummaries?.length
        ),
      }))

      .filter((service) => service.quoteItems?.length);

    if (!quoteServicesWithoutSummaries.length) {
      return;
    }

    loadRealisedQuantities(quoteServicesWithoutSummaries);
  }, [servicesList, invoiceItems]);

  const handleExchangeRateChange = (currencyId, value) => {
    const invoiceCurrencyId = Number(formData.currencyId.value);

    setExchangeRates((prev) => {
      const exists = prev.some(
        (rate) =>
          Number(rate.currencyFrom) === Number(currencyId) &&
          Number(rate.currencyTo) === invoiceCurrencyId
      );

      if (exists) {
        return prev.map((rate) =>
          Number(rate.currencyFrom) === Number(currencyId) &&
          Number(rate.currencyTo) === invoiceCurrencyId
            ? {
                ...rate,
                exchangeRate: value,
              }
            : rate
        );
      }

      return [
        ...prev,
        {
          id: null,
          currencyFrom: currencyId,
          currencyTo: invoiceCurrencyId,
          exchangeRate: value,
        },
      ];
    });
    setExchangeRateErrors((prev) => {
      const updated = { ...prev };
      delete updated[Number(currencyId)];
      return updated;
    });
  };

  const fetchServices = (siteId) => {
    axios
      .get(`/sites/${siteId}/services?quoteStatus=3&orderColumn=isActive`)
      .then((response) => {
        setServicesList(response.data || []);
        setServiceLoading(false);
      })
      .catch(() => setServicesList([]))
      .finally();
  };

  useEffect(() => {
    const siteId = formData.site?.value?.id;
    if (!siteId) {
      setServicesList([]);
      return;
    }
    setServiceLoading(true);
    fetchServices(siteId);
  }, [formData.site?.value?.id]);

  const statusOptions = getInvoiceStatusOptions(
    t,
    statusId,
    formData.documentType.value
  );
  const selectedStatus = getInvoiceStatus(t, formData.statusId.value);

  const initializeExchangeCurrencies = (
    services,
    invoiceCurrencyId,
    existingExchangeRates = []
  ) => {
    const currencies = new Map();

    const invoiceCurrency = Number(invoiceCurrencyId);

    services.forEach((service) => {
      const currency = service.quoteService?.currency;

      if (!currency) {
        return;
      }

      const currencyId = Number(currency.id);

      // Same currency as invoice → no exchange rate
      if (currencyId === invoiceCurrency) {
        return;
      }

      const hasQuantity = service.quoteItems?.some(
        (item) =>
          item.quantity !== null &&
          item.quantity !== "" &&
          Number(item.quantity) !== 0
      );

      if (!hasQuantity) {
        return;
      }

      if (currencies.has(currencyId)) {
        return;
      }

      const existingRate = existingExchangeRates.find(
        (rate) =>
          Number(rate.currencyFrom) === currencyId &&
          Number(rate.currencyTo) === invoiceCurrency
      );

      currencies.set(currencyId, {
        currency,
        id: existingRate?.id ?? null,
        exchangeRate:
          existingRate?.exchangeRate !== undefined &&
          existingRate?.exchangeRate !== null
            ? String(existingRate.exchangeRate)
            : "",
      });
    });

    setExchangeCurrencies(Array.from(currencies.values()));
  };

  useEffect(() => {
    if (!serviceItems.length || !formData.currencyId?.value) {
      setExchangeCurrencies([]);
      return;
    }

    initializeExchangeCurrencies(
      serviceItems,
      formData.currencyId.value,
      exchangeRates
    );
  }, [serviceItems, formData.currencyId?.value, exchangeRates]);

  const handleQuantityChange = (serviceId, quoteItemId, value) => {
    setServiceItems((prev) =>
      prev.map((service) => {
        if (service.quoteService.id !== serviceId) {
          return service;
        }
        return {
          ...service,
          quoteItems: service.quoteItems.map((item) =>
            item.quoteItemId === quoteItemId
              ? {
                  ...item,
                  quantity: value,
                }
              : item
          ),
        };
      })
    );
    setServiceSectionError([]);
    setServiceItemsErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };

      if (updatedErrors[serviceId]) {
        updatedErrors[serviceId] = { ...updatedErrors[serviceId] };

        delete updatedErrors[serviceId][quoteItemId];

        if (Object.keys(updatedErrors[serviceId]).length === 0) {
          delete updatedErrors[serviceId];
        }
      }

      return updatedErrors;
    });
  };

  const getClientInvoiceItemSummary = (summary, quoteItem) => {
    const hesusService = quoteItem?.hesusService || {};

    const {
      needSupplierPlanning,
      needCenter,
      needCarrier,
      needCombination,
      needCombinationCarrier,
      needCombinationCenter,
    } = hesusService;

    const supplierPlanningLineSummaryId =
      summary?.supplierPlanningLineSummary?.id;

    const centreSummary = summary?.supplierPlanningLineSummary?.centreSummary;

    const haulierSummary = summary?.supplierPlanningLineSummary?.haulierSummary;

    const otherServiceSummary = summary?.otherServiceSummary;

    // 1. Other service
    if (!needSupplierPlanning) {
      return {
        otherServiceSummary,
        quantity: otherServiceSummary?.quantity,
      };
    }

    // 2. Treatment + Transport together
    if (needCenter && needCarrier && !needCombination) {
      return {
        supplierPlanningLineSummaryId,
        centreSummary,
        haulierSummary,
        quantity:
          quoteItem.unit === "LOAD"
            ? haulierSummary?.quantity
            : centreSummary?.quantity,
      };
    }

    // 3. Treatment only
    if (needCenter && !needCarrier && !needCombination) {
      return {
        supplierPlanningLineSummaryId,
        centreSummary,
        quantity: centreSummary?.quantity,
      };
    }

    // 4. Transport only
    if (needCarrier && !needCenter && !needCombination) {
      return {
        supplierPlanningLineSummaryId,
        haulierSummary,
        quantity: haulierSummary?.quantity,
      };
    }

    // 5. Treatment + Transport separated - Treatment
    if (needCombination && needCenter && needCombinationCarrier) {
      return {
        supplierPlanningLineSummaryId,
        centreSummary,
        quantity: centreSummary?.quantity,
      };
    }

    // 6. Treatment + Transport separated - Transport
    if (needCombination && needCarrier && needCombinationCenter) {
      return {
        supplierPlanningLineSummaryId,
        haulierSummary,
        quantity: haulierSummary?.quantity,
      };
    }

    return null;
  };

  const parseDecimal = (value) => {
    return Number(String(value).replace(",", "."));
  };
  const getFormData = () => {
    const invoiceItemsInfo = [
      // Existing quote items
      ...serviceItems.flatMap((service) =>
        service.quoteItems
          .filter(
            (item) =>
              item.quantity !== null &&
              item.quantity !== "" &&
              item.quantity !== undefined &&
              Number(String(item.quantity).replace(",", ".")) !== 0
          )
          .map((item) => {
            const realisedQuantityData = item.realisedQuantityData;

            const clientInvoiceItemSummaries = realisedQuantityData
              ? realisedQuantityData.summaries
                  ?.filter((summary) => summary.selected === true)
                  .map((summary) => getClientInvoiceItemSummary(summary, item))
                  .filter(Boolean) || []
              : null;

            return {
              id: item.id,
              quoteService: {
                id: service.quoteService.id,
                currency: service.quoteService.currency,
              },
              quoteItem: { id: item.quoteItemId },
              serviceLabel: item.quoteItemLabel,
              invoicedUnit: item.invoicedUnit,
              invoicedQuantity: parseDecimal(item.quantity),
              sellingPrice: item.sellingPrice,
              sellingUnit: item.sellingUnit,
              quantityRealised: item.quantityRealised,
              unitRealised: item.unitRealised,
              isExtra: false,

              clientInvoiceItemSummariesTotal: realisedQuantityData
                ? realisedQuantityData.totalSelected
                : item.quantityRealised,

              clientInvoiceItemSummaries,

              createdBy: { id: userInfo.id },
              updatedBy: { id: userInfo.id },
            };
          })
      ),

      // Additional / extra items
      ...(extraItems ?? [])
        .filter(
          (item) =>
            item.quantity !== null &&
            item.quantity !== "" &&
            item.quantity !== undefined &&
            Number(String(item.quantity).replace(",", ".")) !== 0
        )
        .map((item) => ({
          id: item.id,
          quoteService: null,
          quoteItem: null,
          serviceLabel: item.serviceLabel,
          clientComment: item.clientComment,
          invoicedUnit: item.sellingUnit,
          invoicedQuantity: parseDecimal(item.quantity),
          sellingPrice: parseDecimal(item.sellingPrice),
          sellingUnit: item.sellingUnit,
          quantityRealised: item.quantityRealised ?? 0,
          unitRealised: item.sellingUnit,
          isExtra: true,
          hesusCatalog: { id: item.hesusCatalogId },
          createdBy: { id: userInfo.id },
          updatedBy: { id: userInfo.id },
        })),
    ];
    const invoiceExchangeRates = (exchangeCurrencies ?? []).map((currency) => ({
      id: currency.id,
      currencyFrom: currency.currency.id,
      currencyTo: formData.currencyId.value,
      exchangeRate: parseDecimal(currency.exchangeRate),
    }));

    return {
      id: formData.id.value,
      documentType: formData.documentType.value,
      company: { id: formData.company.value?.id },
      site: { id: formData.site.value?.id },
      attachedInvoice: { id: formData.attachedInvoice.value?.id },
      country: { id: formData.countryId.value },
      currency: { id: formData.currencyId.value },
      periodStart: formData.startDate.value
        ? moment(formData.startDate.value).format("YYYY-MM-DD")
        : "",
      periodEnd: formData.endDate.value
        ? moment(formData.endDate.value).format("YYYY-MM-DD")
        : "",
      dueDate: formData.dueDate.value
        ? moment(formData.dueDate.value).format("YYYY-MM-DD")
        : "",
      isSentByEmail: formData.sendByEmail.value,
      linkToSummaries: formData.linkToSummaries.value,
      hasAppendedSummaries: formData.hasAppendedSummaries.value,
      clientCommentBefore: descriptionData.clientCommentBefore,
      clientCommentAfter: descriptionData.clientCommentAfter,
      internalComment: descriptionData.internalComment,
      clientOrderNumber: formData.orderNumber.value,
      isSubcontracting: formData.isSubcontracting.value,
      isInterco: formData.invoiceType.value.includes(1),
      isRegularisation: formData.invoiceType.value.includes(2),
      vatRate: { id: formData.vatRateId.value },
      bankAccount: { id: formData.bankAccountId.value },
      paymentTerm: { id: formData.paymentTermId.value },
      status: formData.statusId.value,
      createdBy: { id: userInfo.id },
      updatedBy: { id: userInfo.id },
      recipients: (formData.recipients?.value || [])
        .filter((recipient) => recipient?.contactId || recipient?.id)
        .map((recipient) => ({
          id: recipient?.id,
          contactId: recipient.contactId,
        })),
      delegatedCompany:
        formData.delegatedCompany?.value?.id != null
          ? { id: formData.delegatedCompany.value.id }
          : null,
      items: invoiceItemsInfo,
      exchangeRates: invoiceExchangeRates,
    };
  };

  const parsedQuantity = (value) => {
    if (value === null || value === undefined || value === "") {
      return value;
    }

    return Number(String(value).replace(",", "."));
  };

  const validateExtraItems = () => {
    const errors = extraItems.map((item) => {
      const itemErrors = {};

      // Check whether any field contains a value
      const hasData = [
        item.quantity,
        item.sellingPrice,
        item.sellingUnit,
        item.isFreeText ? item.serviceLabel : item.hesusCatalogId,
      ].some((value) => value !== null && value !== undefined && value !== "");

      // Ignore completely empty extra items
      if (!hasData) {
        return itemErrors;
      }

      // Quantity required and must not be zero
      const quantity = parsedQuantity(item.quantity);

      if (
        item.quantity === null ||
        item.quantity === undefined ||
        item.quantity === "" ||
        !Number.isFinite(quantity) ||
        quantity === 0
      ) {
        itemErrors.quantity = t("GENERAL_MANDATORY_ERROR");
      }

      // Selling price required
      const sellingPrice = parsedQuantity(item.sellingPrice);
      if (
        item.sellingPrice === null ||
        item.sellingPrice === undefined ||
        item.sellingPrice === "" ||
        !Number.isFinite(sellingPrice)
      ) {
        itemErrors.sellingPrice = t("GENERAL_MANDATORY_ERROR");
      }

      // Selling unit required
      if (
        item.sellingUnit === null ||
        item.sellingUnit === undefined ||
        item.sellingUnit === ""
      ) {
        itemErrors.sellingUnit = t("GENERAL_MANDATORY_ERROR");
      }

      // Free text → serviceLabel required
      if (item.isFreeText) {
        if (
          item.serviceLabel === null ||
          item.serviceLabel === undefined ||
          item.serviceLabel === ""
        ) {
          itemErrors.serviceLabel = t("GENERAL_MANDATORY_ERROR");
        }
      }
      // Normal item → hesusCatalogId required
      else if (
        item.hesusCatalogId === null ||
        item.hesusCatalogId === undefined ||
        item.hesusCatalogId === ""
      ) {
        itemErrors.hesusCatalogId = t("GENERAL_MANDATORY_ERROR");
      }

      return itemErrors;
    });

    setExtraItemsErrors(errors);

    return errors;
  };

  const validateServiceItems = () => {
    const updatedServiceItemsErrors = {};

    serviceItems.forEach((service) => {
      const serviceId = service.quoteService.id;

      service.quoteItems?.forEach((quoteItem) => {
        const value = quoteItem.quantity;
        const quoteItemId = quoteItem.quoteItemId;

        // Ignore empty quantities; handle required quantities separately
        if (value === null || value === undefined || value === "") {
          return;
        }

        const quantity = parsedQuantity(value);

        // Quantity must be numeric
        if (!Number.isFinite(quantity)) {
          if (!updatedServiceItemsErrors[serviceId]) {
            updatedServiceItemsErrors[serviceId] = {};
          }

          updatedServiceItemsErrors[serviceId][quoteItemId] = t(
            "GENERAL_FORMAT_ERROR"
          );
        }
      });
    });

    setServiceItemsErrors(updatedServiceItemsErrors);

    const hasInvalidServiceItemQuantity = Object.values(
      updatedServiceItemsErrors
    ).some((itemErrors) => Object.keys(itemErrors).length > 0);

    if (hasInvalidServiceItemQuantity) {
      return false;
    }

    const hasValidServiceItem =
      serviceItems?.some((service) =>
        service.quoteItems?.some((quoteItem) => {
          const quantity = parsedQuantity(quoteItem.quantity);

          return (
            quoteItem.quantity !== null &&
            quoteItem.quantity !== undefined &&
            quoteItem.quantity !== "" &&
            Number.isFinite(quantity) &&
            quantity !== 0
          );
        })
      ) || false;

    const hasExtraItemWithQuantity =
      extraItems?.some((item) => {
        const quantity = parsedQuantity(item.quantity);

        return (
          item.quantity !== null &&
          item.quantity !== undefined &&
          item.quantity !== "" &&
          Number.isFinite(quantity) &&
          quantity !== 0
        );
      }) || false;

    const extraItemErrors = validateExtraItems();

    const areExtraItemsValid = extraItemErrors.every(
      (errors) => Object.keys(errors).length === 0
    );

    if (hasExtraItemWithQuantity && !areExtraItemsValid) {
      return false;
    }

    return hasValidServiceItem || hasExtraItemWithQuantity;
  };

  const validateExchangeRates = () => {
    const errors = {};
    const invoiceCurrencyId = Number(formData.currencyId.value);

    (serviceItems ?? []).forEach((service) => {
      const currencyId = Number(service.quoteService?.currency?.id);

      if (currencyId === invoiceCurrencyId) return;

      const exchangeRate = exchangeRates.find(
        (rate) =>
          Number(rate.currencyFrom) === currencyId &&
          Number(rate.currencyTo) === invoiceCurrencyId
      );

      const value = String(exchangeRate?.exchangeRate ?? "").trim();
      const parsedValue = Number(value.replace(",", "."));

      if (!value || !Number.isFinite(parsedValue) || parsedValue <= 0) {
        errors[currencyId] = t("GENERAL_MANDATORY_ERROR");
      }
    });

    setExchangeRateErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const validateServiceItemQuantities = () => {
    const updatedErrors = {};
    serviceItems.forEach((service) => {
      const serviceId = service.quoteService.id;
      service.quoteItems?.forEach((quoteItem) => {
        const value = quoteItem.quantity;
        // Ignore empty quantities
        if (
          value === null ||
          value === undefined ||
          String(value).trim() === ""
        ) {
          return;
        }
        const quantity = parsedQuantity(value);
        // Validate only when a quantity has been entered
        if (!Number.isFinite(quantity)) {
          if (!updatedErrors[serviceId]) {
            updatedErrors[serviceId] = {};
          }
          updatedErrors[serviceId][quoteItem.quoteItemId] = t(
            "GENERAL_FORMAT_ERROR"
          );
        }
      });
    });
    setServiceItemsErrors(updatedErrors);
    return Object.keys(updatedErrors).length === 0;
  };

  const validateForm = (data) => {
    let isFormValid = true;
    const documentType = Number(data.documentType.value);
    const statusId = Number(data.statusId.value);
    const errors = [];

    if (!data.countryId.value) {
      isFormValid = false;
      data.countryId.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.documentType.value) {
      isFormValid = false;
      data.company.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.startDate.value) {
      isFormValid = false;
      data.startDate.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.endDate.value) {
      isFormValid = false;
      data.endDate.error = t("GENERAL_MANDATORY_ERROR");
    }

    // validation condition based
    const isCompanySiteRequired =
      (documentType === 1 && [1, 2, 10].includes(statusId)) ||
      (documentType === 2 && [1, 2].includes(statusId));

    if (isCompanySiteRequired && !data.company.value?.id) {
      isFormValid = false;
      data.company.error = t("CLIENT_INVOICING_ERROR_NO_CLIENT");
    }

    if (
      [1, 2].includes(documentType) &&
      [1, 2].includes(statusId) &&
      data.sendByEmail.value
    ) {
      const recipientList = Array.isArray(data.recipients.value)
        ? data.recipients.value
        : [];
      // Status 2 → recipient is mandatory
      if (
        statusId === 2 &&
        (!Array.isArray(recipientList) || recipientList.length === 0)
      ) {
        isFormValid = false;
        data.recipients.error = t("CLIENT_INVOICE_ERROR_NO_INVOICE_CONTACT");
      }

      // If recipients exist → validate email
      if (Array.isArray(recipientList) && recipientList?.length > 0) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        recipientList.forEach((recipient) => {
          const recipientEmail = recipient.contactInfo?.email
            ? recipient.contactInfo?.email
            : recipient.email;
          if (!recipientEmail || !emailRegex.test(recipientEmail.trim())) {
            isFormValid = false;
            data.recipients.error = t("CLIENT_INVOICE_ERROR_CONTACT_NO_EMAIL");
          }
        });
      }
    }
    if (documentType === 1 && [2, 10].includes(statusId)) {
      if (!data.currencyId.value) {
        isFormValid = false;
        data.currencyId.error = t("GENERAL_MANDATORY_ERROR");
      }
      if (!data.bankAccountId.value) {
        isFormValid = false;
        data.bankAccountId.error = t("GENERAL_MANDATORY_ERROR");
      }
      if (!data.paymentTermId.value) {
        isFormValid = false;
        data.paymentTermId.error = t("GENERAL_MANDATORY_ERROR");
      }
      if (!data.vatRateId.value) {
        isFormValid = false;
        data.vatRateId.error = t("GENERAL_MANDATORY_ERROR");
      }
      if (!data.dueDate.value) {
        isFormValid = false;
        data.dueDate.error = t("GENERAL_MANDATORY_ERROR");
      }
    }
    // Document type 2 + status 2
    if (documentType === 2 && statusId === 2) {
      if (!data.currencyId.value) {
        isFormValid = false;
        data.currencyId.error = t("GENERAL_MANDATORY_ERROR");
      }
    }

    if (
      (documentType === 1 && [1, 2, 10].includes(statusId)) ||
      (documentType === 2 && [1, 2].includes(statusId))
    ) {
      const hasItems = serviceItems?.length > 0 || extraItems?.length > 0;
      if (!hasItems) {
        isFormValid = false;
        errors.push(t("CLIENT_INVOICING_ERROR_NO_SERVICES"));
      } else {
        serviceItems?.length > 0 || extraItems?.length > 0;
        const isServiceItemsValid = validateServiceItems();
        if (!isServiceItemsValid) {
          isFormValid = false;
          errors.push(t("CLIENT_INVOICE_ERROR_SERVICES_NO_QUANTITIES"));
        }
      }
      setServiceSectionError(errors);
    }
    const extraItemErrors = validateExtraItems();
    const areExtraItemsValid = extraItemErrors.every(
      (errors) => Object.keys(errors).length === 0
    );
    if (!areExtraItemsValid) {
      isFormValid = false;
    }

    const isExchangeRateValid = validateExchangeRates();
    if (!isExchangeRateValid) {
      isFormValid = false;
    }

    // Validate entered service-item quantities for every status.
    // Empty quantities are allowed, including for drafts.
    const areServiceItemQuantitiesValid = validateServiceItemQuantities();

    if (!areServiceItemQuantitiesValid) {
      isFormValid = false;
    }

    if (!isFormValid) {
      setFormData({ ...data });
    }

    return isFormValid;
  };

  useEffect(() => {
    if (isConfirm) {
      onFormSubmit();
    }
    setActionConfirmStatus(false);
  }, [isConfirm]);

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      if (
        [0, 1, 10].includes(formData.statusId.value) &&
        formData.company.value?.id !== formData.site.value?.company?.id
      ) {
        setConfirmVisibility(true);
      } else {
        onFormSubmit();
      }
    }
  };
  const onFormSubmit = () => {
    const payload = getFormData();
    setLoadingInvoice(true);
    setApiError("");
    const request = isEditMode
      ? axios.put(`/clientInvoices/${invoiceId}`, payload)
      : axios.post("/clientInvoices", payload);
    request
      .then((response) => {
        const newId = response?.data[0]?.id;
        const newStatus = Number(response?.data[0]?.status);
        setStatusId(newStatus);
        if (location.state?.companyId) {
          history(`/company/view/${location.state.companyId}/invoices`);
        } else if ([0, 1, 10].includes(newStatus)) {
          history(`/invoice/edit/${newId}`);
        } else if ([2, 3, 4, 5, 6, 7, 8, 9].includes(newStatus)) {
          history(`/invoice/view/${newId}`);
        } else {
          history(`/invoice/edit/${newId}`);
        }
      })
      .catch((error) => {
        console.error("Submit error:", error);

        setApiError(error.response?.data?.message ?? "Failed to save invoice");
      })
      .finally(() => setLoadingInvoice(false));
  };

  const onPopUpCancel = () => {
    setConfirmVisibility(false);
    setActionConfirmStatus(false);
  };

  const onPopUpConfirm = () => {
    setConfirmVisibility(false);
    setActionConfirmStatus(true);
  };

  if (isNotFound && isEditMode) {
    return <NotFound />;
  }

  return (
    <MainViewContainer className="add-edit-invoice">
      <SectionContainer>
        <OuterSection $isLoading={loadingInvoice}>
          <GridRow column="1fr auto auto " padding="6px 0px">
            <Title>
              {!isEditMode && t("CLIENT_INVOICING_NEW_DOCUMENT")}
              {isEditMode &&
                `${
                  formData.documentType.value === 1
                    ? t("CLIENT_INVOICING_DOCUMENT_TYPE_INVOICE")
                    : t("CLIENT_INVOICING_DOCUMENT_TYPE_CREDIT_NOTE")
                } #${formData.documentNumber.value ?? formData.id.value}`}
            </Title>
            <Button
              disabled={isEditMode && loadingInvoice}
              styles={{
                color: "white",
                background: "#1BACE4",
                border: "1px solid #1BACE4",
                "&:hover": {
                  backgroundColor: "#1589b6",
                },
              }}
              onClick={onSubmit}
            >
              <>
                {isSubmitting && <ClipLoader size={15} color="#fff" />}
                {!isSubmitting && t("CLIENT_INVOICE_SAVE")}
              </>
            </Button>
            {apiError && <RequiredMessagePara>{apiError}</RequiredMessagePara>}
          </GridRow>
          <InvoiceDocument
            formData={formData}
            onFormChange={onFormChange}
            setFormData={setFormData}
            statusOptions={statusOptions}
            selectedStatus={selectedStatus}
            isEditMode={isEditMode}
          />
          <CompanySite
            formData={formData}
            onFormChange={onFormChange}
            isEditMode={isEditMode}
          />
          {Object.keys(generalParams).length > 0 &&
            formData.countryId.value !== "" && (
              <FinancialInformation
                formData={formData}
                onFormChange={onFormChange}
                generalParams={generalParams}
                vatRateList={vatRateList}
                setVatRateList={setVatRateList}
                paymentTermsList={paymentTermsList}
                setPaymentTermsList={setPaymentTermsList}
              />
            )}
          <AdditionalDocumentInformation
            formData={formData}
            onFormChange={onFormChange}
            descriptionData={descriptionData}
            setDescriptionData={setDescriptionData}
            isEditMode={isEditMode}
            isCommentDataLoaded={isCommentDataLoaded}
          />
          {Object.keys(generalParams).length > 0 &&
            formData.countryId.value !== "" && (
              <InvoiceServices
                serviceItems={serviceItems}
                setServiceItems={setServiceItems}
                extraItems={extraItems}
                currencyPosition={
                  countryCurrencyPosition[formData.currencyId.value]
                }
                countryId={formData.countryId.value}
                currencyId={formData.currencyId.value}
                exchangeCurrencies={exchangeCurrencies}
                exchangeRates={exchangeRates}
                handleExchangeRateChange={handleExchangeRateChange}
                handleQuantityChange={handleQuantityChange}
                vatRateList={vatRateList}
                vatRateId={formData.vatRateId?.value}
                onAddExtraItem={handleAddExtraItem}
                onDeleteExtraItem={handleDeleteExtraItem}
                handleExtraItemChange={handleExtraItemChange}
                handleFreeTextClick={handleFreeTextClick}
                hesusCatalogList={hesusCatalogList}
                unitList={unitList}
                serviceLoading={serviceLoading}
                serviceSectionError={serviceSectionError}
                extraItemsErrors={extraItemsErrors}
                realisedQuantityLoading={realisedQuantityLoading}
                startDate={
                  formData.startDate.value
                    ? moment(formData.startDate.value).format("YYYY-MM-DD")
                    : ""
                }
                endDate={
                  formData.endDate.value
                    ? moment(formData.endDate.value).format("YYYY-MM-DD")
                    : ""
                }
                handleRealisedQuantityPopupOpen={
                  handleRealisedQuantityPopupOpen
                }
                documentType={formData.documentType.value}
                serviceItemsErrors={serviceItemsErrors}
                exchangeRateErrors={exchangeRateErrors}
              />
            )}
          <GridRow background="none"></GridRow>
          <GridRow column="1fr auto auto " padding="6px 0px" background="none">
            <div></div>
            <Button
              disabled={isEditMode && loadingInvoice}
              styles={{
                color: "white",
                background: "#1BACE4",
                border: "1px solid #1BACE4",
                "&:hover": {
                  backgroundColor: "#1589b6",
                },
              }}
              onClick={onSubmit}
            >
              <>
                {isSubmitting && <ClipLoader size={15} color="#fff" />}
                {!isSubmitting && t("CLIENT_INVOICE_SAVE")}
              </>
            </Button>
            {apiError && <RequiredMessagePara>{apiError}</RequiredMessagePara>}
          </GridRow>
          <GridRow background="none"></GridRow>
          <GridRow background="none"></GridRow>
        </OuterSection>
        {isConfirmVisible && (
          <WarningPopUp
            title={t("CLIENT_INVOICING_COMPANY_MISMATCH_TITLE")}
            message={t("CLIENT_INVOICING_COMPANY_MISMATCH_CONTENT")}
            onPopUpCancel={onPopUpCancel}
            onPopUpConfirm={onPopUpConfirm}
            ValidateContent={t("VALIDATE")}
            CancelContent={t("CANCEL")}
          />
        )}
      </SectionContainer>
    </MainViewContainer>
  );
};

AddEditInvoice.propTypes = {
  t: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  updateRefreshStatus: PropTypes.func,
};

AddEditInvoice.defaultProps = {
  isEditMode: false,
  id: null,
  updateRefreshStatus: null,
};

export default withTranslation()(AddEditInvoice);
